import React, { useState } from 'react';

// Design System Components
import Button from 'Components/DesignSystem/Button/Button';
import TextField from 'Components/DesignSystem/TextField/TextField';
import Typography from 'Components/DesignSystem/Typography/Typography';
import { ArrowLeft, Edit } from 'Components/DesignSystem/Iconography/Icons';
import IconButton from 'Components/DesignSystem/IconButton/IconButton';
import Skeleton from 'Components/DesignSystem/Skeleton/Skeleton';

// Constants
import { MAX_CARD_NAME_LENGTH, STRINGS } from '../../constants';

interface EditorHeaderProps {
  name: string;
  onBack: () => void;
  onRename: (name: string) => void;
  /**
   * The save indicator. Passed in rather than read from context here so this
   * component stays a presentational header — and so a test can render it in
   * any save state without standing up a repository.
   */
  saveIndicator?: React.ReactNode;
  /**
   * True while the card is still being fetched. The header stays MOUNTED for it
   * (2026-09-16): the back control is the user's way out, and removing it for the
   * length of a slow request took the exit away exactly when it was most wanted.
   * The name is a skeleton, and the two controls that act on a card that has not
   * arrived — rename, save indicator — are not drawn.
   */
  loading?: boolean;
}

/**
 * The Card Editor header: a back control, the card name, and a pencil that turns
 * the name into an editable field (spec R28, R29, R30).
 *
 * Confirmation is an explicit action rather than a blur, so clicking Cancel
 * cannot be read as "confirm" on the way past.
 */
/** One class list, so the loading header and the loaded one are the same box. */
const HEADER_CLASS = 'tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-border-b tw-border-edge';

const EditorHeader = ({ name, onBack, onRename, saveIndicator, loading = false }: EditorHeaderProps) => {
  const [isRenaming, setIsRenaming] = useState(false);
  const [draftName, setDraftName] = useState(name);

  const startRenaming = () => {
    setDraftName(name);
    setIsRenaming(true);
  };

  const confirmRename = () => {
    const trimmed = draftName.trim();
    if (trimmed !== '') {
      onRename(trimmed);
    }
    setIsRenaming(false);
  };

  const backButton = (
    <IconButton
      variant="subtle"
      size="sm"
      onClick={onBack}
      aria-label={STRINGS.editor.back}
      title={STRINGS.editor.back}
    >
      <ArrowLeft size={16} aria-hidden="true" />
    </IconButton>
  );

  if (loading) {
    return (
      <header className={HEADER_CLASS}>
        {backButton}
        {/* 32px is the line box `display` renders in and 28px is the `sm` IconButton
            beside it — measured, not guessed, so the name and the pencil land where
            their shapes already were. Both are aria-hidden: the skeleton says nothing
            and `EditorSkeleton`'s status is the page's one voice.

            **Size a `Skeleton` with its `width`/`height` PROPS, never a `tw-w-*` /
            `tw-h-*` class.** It always writes `width: 100%` inline when no `width`
            prop is given, and an inline style beats any class — so a classed width
            silently loses. Measured here first: the pencil took the whole header row
            and squeezed this name to **0px**. */}
        <Skeleton
          variant="rect"
          width="192px"
          height="32px"
          className="tw-shrink-0"
          data-testid="card-editor-header-name-skeleton"
        />
        <Skeleton variant="rect" width="28px" height="28px" className="tw-shrink-0" />
      </header>
    );
  }

  return (
    <header className={HEADER_CLASS}>
      {backButton}

      {isRenaming ? (
        <div className="tw-flex tw-items-center tw-gap-2 tw-flex-1 tw-min-w-0">
          <div className="tw-flex-1 tw-min-w-0 tw-max-w-[280px]">
            <TextField
              label=""
              value={draftName}
              onChange={setDraftName}
              maxLength={MAX_CARD_NAME_LENGTH}
              placeholder={STRINGS.editor.renameLabel}
            />
          </div>
          <Button variant="primary" showIcon={false} onClick={confirmRename}>
            {STRINGS.editor.renameConfirm}
          </Button>
          <Button variant="tertiary" showIcon={false} onClick={() => setIsRenaming(false)}>
            {STRINGS.newCardDialog.cancel}
          </Button>
        </div>
      ) : (
        <>
          {/* § 285a — the shared 32rem cap: a long card name must not push the rename
              affordance away from it. See RuleHeader for the reasoning. */}
          <Typography variant="display" as="h1" className="tw-text-ink tw-truncate tw-max-w-[32rem]">
            {name}
          </Typography>
          <IconButton
            variant="subtle"
            size="sm"
            onClick={startRenaming}
            aria-label={STRINGS.editor.rename}
            title={STRINGS.editor.rename}
          >
            <Edit size={16} aria-hidden="true" />
          </IconButton>
          {/* Pushed to the trailing edge with `ms-auto` (margin-inline-start),
              not `ml-auto`: the logical property follows the writing direction,
              so this lands correctly under `dir="rtl"` too. */}
          <div className="tw-ms-auto">{saveIndicator}</div>
        </>
      )}
    </header>
  );
};

export default EditorHeader;
